import { ListsInclusions } from '@app/data/clients/rbServerSideClient/queries/GetListInclusions/types';
import { ProcessedSearchResultItem } from '@app/data/clients/rbServerSideClient/queries/GetSearchResults/types';
import { Shop_Lists_Entity } from '@app/data/shop-bff';
import { useViewportWidth } from '@redbubble/next/hooks';
import logger from '@redbubble/next/logger';
import { DeviceType, UserInfo } from '@redbubble/next/types';
import { useUserInfo } from '@redbubble/next-provider';
import router from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ListsModal } from '../components/ListsModal/ListsModal';
import {
  FAVORITE_URL_PARAM_NAME,
  ITEM_TO_FAVORITE,
  RETURN_FAVORITE_TOKEN_NAME,
  verifyReturnToken,
} from '../components/SignUpGuard/utils';
import { AddToListToast } from '../components/Toasts/AddToListToast';
import {
  ListsButtonInventoryItem,
  ListsInclusionsLists,
} from '../types/shop-bff/types';
import {
  addItemToDefaultList,
  addItemToList,
  createNewList,
  itemIsIncludedInAnyList,
  removeItemFromList,
  updateListItem,
  useUserLists,
} from '../utils/lists';
import { useSignUpGuardContext } from './SignUpGuardContext';

const loadToast = () => import('react-toastify').then((module) => module.toast);

const showAddToListToast = async (
  inventoryItem: ListsButtonInventoryItem | ProcessedSearchResultItem,
  handleOpenModal: () => void,
  isMobile: boolean,
  listName: string
) => {
  const toast = await loadToast();

  if (isMobile) {
    toast.dismiss();
  }
  toast(
    <AddToListToast
      inventoryItem={inventoryItem}
      handleOpenModal={handleOpenModal}
      listName={listName}
    />,
    {
      closeButton: false,
    }
  );
};

export const useAddToList = ({
  inventoryItem,
  initialListsInclusions,
  onAddToList,
}: {
  inventoryItem: ListsButtonInventoryItem | ProcessedSearchResultItem;
  initialListsInclusions?: ListsInclusions | null;
  onAddToList?: () => void;
}) => {
  const { id: inventoryItemId } = inventoryItem;

  const { signUpGuard, setSignUpGuard } = useSignUpGuardContext();
  const [{ locale, isLoggedIn, userId }] = useUserInfo<UserInfo>();

  const { isOrAbove } = useViewportWidth();
  const isMobile = !isOrAbove(DeviceType.Tablet);

  const [openModal, setOpenModal] = useState(false);
  const [itemIsFavorited, setItemIsFavorited] = useState<boolean>(false);
  const [listsInclusions, setListInclusions] = useState(initialListsInclusions);

  const checkboxQueue = useRef<Map<string, boolean>>(new Map());

  const { userLists, loading, refresh, setUserLists } = useUserLists();

  // biome-ignore lint/correctness/useExhaustiveDependencies: useExhaustiveDependencies
  const handleOpenModal = useCallback(() => {
    if (userId) {
      refresh({
        input: {
          userId: userId.toString(),
          locale,
        },
      });
      setOpenModal(true);
    }
  }, [userId, locale, setOpenModal, refresh]);

  const updateItemListInclusions = useCallback(
    (newListInclusions: ListsInclusionsLists) => {
      setListInclusions((prevState) => {
        const updatedListInclusions = prevState?.map((listItem) => {
          if (
            listItem.id ===
            `${Shop_Lists_Entity.InventoryItem}_${inventoryItemId}`
          ) {
            const tempListInclusions = updateListItem(
              newListInclusions,
              checkboxQueue
            );
            return {
              ...listItem,
              lists: tempListInclusions,
            };
          }
          return listItem;
        });

        // XXX(ci): If updatedListInclusions is empty then we can replace the state completely.
        // The shop-BFF-shaped newListInclusions is assigned into
        // ListsInclusions here for the same __typename-mismatch reason as
        // tempUpdateListInclusions; it will fall out once the remaining
        // lists mutations are migrated off shop-BFF.
        if (updatedListInclusions?.length === 0)
          return [
            {
              id: `${Shop_Lists_Entity.InventoryItem}_${inventoryItemId}`,
              lists: newListInclusions,
            },
          ];
        return updatedListInclusions;
      });
    },
    [inventoryItemId]
  );

  const handleOnToggleList = useCallback(() => {
    if (!isLoggedIn) {
      setSignUpGuard({
        ...signUpGuard,
        open: true,
        actionToken: RETURN_FAVORITE_TOKEN_NAME,
        itemToFavorite: inventoryItemId,
      });
      return;
    }

    if (listsInclusions) {
      if (!itemIsFavorited) {
        addItemToDefaultList(inventoryItemId, locale)
          .then((newListInclusions) => {
            if (newListInclusions) {
              const { defaultListName } = newListInclusions;
              updateItemListInclusions(newListInclusions.lists);
              return defaultListName;
            }
            throw new Error('Failed to add item to default list');
          })
          .then((defaultListName) => {
            showAddToListToast(
              inventoryItem,
              handleOpenModal,
              isMobile,
              defaultListName
            );
          })
          .then(onAddToList)
          .catch((error: Error) => {
            logger.error(
              {
                err: error,
                message: error.message,
                context: 'useAddToList',
              },
              error.message
            );
          });
      } else {
        handleOpenModal();
      }
    }
  }, [
    isLoggedIn,
    listsInclusions,
    setSignUpGuard,
    signUpGuard,
    inventoryItemId,
    itemIsFavorited,
    locale,
    onAddToList,
    updateItemListInclusions,
    inventoryItem,
    handleOpenModal,
    isMobile,
  ]);

  const handleCreateNewList = useCallback(
    async (name: string) => {
      const newList = await createNewList(name);

      if (newList) {
        const newListInclusion = await addItemToList(
          inventoryItemId,
          newList.listId,
          locale
        );
        updateItemListInclusions(newListInclusion);

        setUserLists((prevState) => ({
          userLists: {
            id: prevState?.userLists?.id || '',
            defaultList: newList,
            lists: {
              nodes: [newList, ...(prevState?.userLists?.lists?.nodes ?? [])],
            },
          },
          loading: false,
        }));
      }

      return newList;
    },
    [updateItemListInclusions, setUserLists, inventoryItemId, locale]
  );

  const tempUpdateListInclusions = useCallback(
    (listId: string) => {
      setListInclusions((prevState) => {
        return (prevState || []).map((listItem) => {
          if (
            listItem.id ===
            `${Shop_Lists_Entity.InventoryItem}_${inventoryItemId}`
          ) {
            return {
              ...listItem,
              lists: listItem.lists?.some((list) => list.listId === listId)
                ? listItem.lists.filter((list) => list.listId !== listId)
                : [
                    ...(listItem.lists || []),
                    { listId } as (typeof listItem.lists & object)[number],
                  ],
            };
          }
          return listItem;
          // ListsInclusions is now derived from the direct lists service
          // (__typename: 'Inclusion' / 'ListId'), but the mutation responses
          // that feed this state are still shop-BFF
          // (__typename: 'shop_lists_*'). The shapes are structurally
          // identical, so the cast is safe; it will fall out once the
          // remaining lists mutations are migrated off shop-BFF.
        }) as ListsInclusions;
      });
    },
    [inventoryItemId]
  );

  const updateQueue = (
    listId: string,
    newListInclusions: ListsInclusionsLists
  ) => {
    const queueItem = checkboxQueue.current.get(listId);
    const newListItem = newListInclusions.find(
      (list) => list.listId === listId
    );
    if ((queueItem && newListItem) || (!queueItem && !newListItem)) {
      checkboxQueue.current.delete(listId);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: useExhaustiveDependencies
  const handleCheckbox = useCallback(
    (id: string, listId: string, checked: boolean) => {
      checkboxQueue.current.set(listId, !checked);

      tempUpdateListInclusions(listId);
      if (!checked) {
        addItemToList(id, listId, locale).then((newListInclusions) => {
          updateQueue(listId, newListInclusions);
          updateItemListInclusions(newListInclusions);
        });
      } else {
        removeItemFromList(id, listId, locale).then((newListInclusions) => {
          updateQueue(listId, newListInclusions);
          updateItemListInclusions(newListInclusions);
        });
      }
    },
    [tempUpdateListInclusions, locale, updateItemListInclusions]
  );

  // for client listsInclusions calls to ensure initial list update occurs
  useEffect(() => {
    if (initialListsInclusions) {
      setListInclusions(initialListsInclusions);
    }
  }, [initialListsInclusions]);

  // Handle return from login/signup flow
  useEffect(() => {
    const {
      [FAVORITE_URL_PARAM_NAME]: addToListToken,
      [ITEM_TO_FAVORITE]: itemToFavorite,
    } = router.query;
    if (!itemToFavorite) return;

    if (listsInclusions && itemToFavorite === inventoryItemId) {
      const newQuery = router.query;
      delete newQuery[FAVORITE_URL_PARAM_NAME];
      delete newQuery[ITEM_TO_FAVORITE];

      if (typeof addToListToken === 'string' && addToListToken) {
        const addToListMatches = verifyReturnToken(
          addToListToken,
          RETURN_FAVORITE_TOKEN_NAME
        );

        if (addToListMatches && !itemIsFavorited) {
          handleOnToggleList();
        }

        router.replace(
          {
            query: newQuery,
          },
          undefined,
          { shallow: true }
        );
      }
    }
  }, [listsInclusions, handleOnToggleList, itemIsFavorited, inventoryItemId]);

  // Check if currently selected item is on list
  useEffect(() => {
    const isItemIncludedFavorited =
      !!listsInclusions &&
      itemIsIncludedInAnyList(inventoryItemId, listsInclusions);

    setItemIsFavorited(isItemIncludedFavorited);
  }, [inventoryItemId, listsInclusions]);

  const Modal = useMemo(() => {
    if (!openModal) {
      return null;
    }
    return (
      <ListsModal
        open={openModal}
        inventoryItem={inventoryItem}
        closeModal={() => setOpenModal(false)}
        handleCheckbox={handleCheckbox}
        handleSubmitForm={handleCreateNewList}
        listsInclusions={listsInclusions}
        userLists={userLists}
        loading={loading}
      />
    );
  }, [
    openModal,
    inventoryItem,
    handleCheckbox,
    listsInclusions,
    userLists,
    loading,
    handleCreateNewList,
  ]);

  return {
    itemIsFavorited,
    handleOnToggleList,
    ListModal: Modal,
  };
};
